import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, MousePointer, Type, Image, Palette, Save, Layers, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    title: 'Welcome to Your Design Studio!',
    description: 'This is where you design your online store. Let me show you how it works — it\'s easier than you think!',
    icon: Sparkles,
    position: 'center',
    highlight: null,
  },
  {
    title: 'Edit Any Text — Just Click!',
    description: 'See any text on the page? Click on it and start typing. That\'s it! Your changes appear instantly.',
    icon: Type,
    position: 'center',
    highlight: null,
  },
  {
    title: 'Replace Images Easily',
    description: 'Hover over any image and click the camera icon to replace it with your own photo. Upload from your phone or computer.',
    icon: Image,
    position: 'center',
    highlight: null,
  },
  {
    title: 'Change Your Brand Colors',
    description: 'Click "Theme" in the toolbar to change your store\'s colors and fonts. Pick colors that match your brand!',
    icon: Palette,
    position: 'top',
    highlight: 'theme-btn',
  },
  {
    title: 'Add New Sections',
    description: 'Want more content? Click "Sections" to browse ready-made layouts — testimonials, product grids, banners, and more.',
    icon: Layers,
    position: 'top',
    highlight: 'sections-btn',
  },
  {
    title: 'Save & Publish',
    description: 'When you\'re happy with your design, click "Save & Go Live" to publish your store. Your customers can see it instantly!',
    icon: Save,
    position: 'top',
    highlight: 'save-btn',
  },
  {
    title: 'You\'re All Set!',
    description: 'Start by clicking on any text to edit it. Don\'t worry — you can always undo changes with Ctrl+Z. Have fun designing!',
    icon: Sparkles,
    position: 'center',
    highlight: null,
  },
];

const STORAGE_KEY = 'vasstr_editor_tour_completed';

const OnboardingTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      // Small delay so editor loads first
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleComplete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, handleComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  if (!show) return null;

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const isFirst = currentStep === 0;
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
              zIndex: 99998, backdropFilter: 'blur(4px)',
            }}
            onClick={handleSkip}
          />

          {/* Tour Card */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 99999,
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '32px',
              width: 'min(440px, 90vw)',
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
            }}
          >
            {/* Skip button */}
            <button
              onClick={handleSkip}
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px', borderRadius: '8px', color: '#9ca3af',
              }}
            >
              <X size={18} />
            </button>

            {/* Progress bar */}
            <div style={{ height: '3px', backgroundColor: '#f3f4f6', borderRadius: '2px', marginBottom: '24px', overflow: 'hidden' }}>
              <motion.div
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
                style={{ height: '100%', backgroundColor: '#d45614', borderRadius: '2px' }}
              />
            </div>

            {/* Icon */}
            <div style={{
              width: '56px', height: '56px', borderRadius: '16px',
              background: 'linear-gradient(135deg, #d45614 0%, #e8774a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <Icon size={28} color="white" />
            </div>

            {/* Content */}
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '8px', lineHeight: 1.3 }}>
              {step.title}
            </h3>
            <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: 1.6, marginBottom: '28px' }}>
              {step.description}
            </p>

            {/* Step indicator */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '24px' }}>
              {TOUR_STEPS.map((_, i) => (
                <div key={i} style={{
                  width: i === currentStep ? '24px' : '8px', height: '8px',
                  borderRadius: '4px',
                  backgroundColor: i === currentStep ? '#d45614' : i < currentStep ? '#f9a880' : '#e5e7eb',
                  transition: 'all 0.3s ease',
                }} />
              ))}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              {!isFirst ? (
                <button onClick={handlePrev} style={{
                  padding: '10px 20px', borderRadius: '10px', border: '1px solid #e5e7eb',
                  backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                  color: '#4b5563', display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <ArrowLeft size={16} /> Back
                </button>
              ) : (
                <button onClick={handleSkip} style={{
                  padding: '10px 20px', borderRadius: '10px', border: 'none',
                  backgroundColor: 'transparent', cursor: 'pointer', fontSize: '14px',
                  color: '#9ca3af',
                }}>
                  Skip Tour
                </button>
              )}

              <button onClick={handleNext} style={{
                padding: '10px 24px', borderRadius: '10px', border: 'none',
                background: 'linear-gradient(135deg, #d45614 0%, #e8774a 100%)',
                color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '6px',
                boxShadow: '0 4px 12px rgba(212,86,20,0.3)',
              }}>
                {isLast ? 'Start Designing!' : 'Next'} {!isLast && <ArrowRight size={16} />}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OnboardingTour;
